import { useRouter } from "next/router";

import React, { useCallback, useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

import { Entity } from "models/entity";
import { twMerge } from "tailwind-merge";

import Modal from "components/Modal";
import SearchInput from "components/SearchInput";

import { RecentComparablesSection } from "../AIWorkflowModalSections";

const EntityComparablesButton = ({
  className,
  entity,
}: {
  className?: string;
  entity?: Entity | null;
}) => {
  const router = useRouter();
  const [showSearchEntityModal, setShowSearchEntityModal] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState<Entity | null>(
    entity || null
  );

  const closeSearchEntityModal = useCallback(() => {
    setShowSearchEntityModal(false);
    setSelectedEntity(entity || null);
  }, [entity]);

  const openSearchEntityModal = useCallback(() => {
    setSelectedEntity(entity || null);
    setShowSearchEntityModal(true);
  }, [entity]);

  const onEntitySearchSelected = useCallback((entity: Entity) => {
    setSelectedEntity(entity);
  }, []);

  const goToSelectedEntityComparables = useCallback(() => {
    if (!selectedEntity?.id) return;

    setShowSearchEntityModal(false);
    router.push(`/tools/comparables?entity-id=${selectedEntity.id}`);
  }, [router, selectedEntity?.id]);

  return (
    <>
      <OverlayTrigger
        placement="bottom"
        overlay={
          <Tooltip>
            <p className="mb-0">
              Generate publicly traded comparables and precedent transactions
            </p>
          </Tooltip>
        }
      >
        <span
          className={twMerge(
            "inline-flex items-center justify-center",
            "cursor-pointer",
            "text-[18px]",
            "hover:bg-[rgba(204,204,204,0.6)] px-[20px] py-[10px] rounded-full",
            className
          )}
          onClick={openSearchEntityModal}
        >
          + Comparables
        </span>
      </OverlayTrigger>

      {showSearchEntityModal && (
        <Modal
          isOpen={showSearchEntityModal}
          shouldCloseOnEsc
          onClose={closeSearchEntityModal}
          className={twMerge("md:w-[624px]")}
          overlayClassName=""
          onAfterOpen={() =>
            document.body.classList.add("!max-h-[calc(100vh-100px)]")
          }
          onAfterClose={() =>
            document.body.classList.remove("!max-h-[calc(100vh-100px)]")
          }
          fullMobilePage
        >
          <div className="p-[24px]">
            <div className="mb-[20px] flex items-start justify-between">
              <div className="flex items-center gap-[10px]">
                <div className="flex h-[32px] w-[32px] items-center justify-center">
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src="https://assets.smartkarma.com/icons/sparkle.svg"
                    alt="AI Workflows"
                    width={20}
                    height={20}
                  />
                </div>
                <div>
                  <div className="text-[16px] font-semibold leading-tight text-[#1f2937]">
                    Generate Company Comparables
                  </div>
                  <div className="mt-[2px] text-[12px] text-[#6b7280]">
                    Powered by Smartkarma AI
                  </div>
                </div>
              </div>
            </div>

            <label className="mb-[6px] block text-[13px] font-medium text-[#1f2937]">
              Company
            </label>
            <SearchInput
              resultPlaceholder="Search by name or Bloomberg Ticker"
              placeholder={
                selectedEntity?.prettyName ||
                "Search by name or ticker (e.g. Tencent, 700 HK)"
              }
              endpoint="/api/v2/entities"
              queryParam="filter[fulltext]"
              onItemSelected={onEntitySearchSelected}
              className="mb-[18px] w-full rounded-[6px] border-[1px] border-solid border-[#d1d5db]"
              inputClassName={(isFocusing) =>
                twMerge(
                  isFocusing ? "" : "border-0",
                  "rounded-[6px] py-[10px] placeholder:text-[13px]"
                )
              }
              iconClassName={() => "text-[#9ca3af]"}
              dropdownClassName="top-[33px] rounded-b-[6px]"
            />

            <div className="mb-[20px] rounded-[6px] bg-[#f9fafb] px-[14px] py-[12px]">
              <div className="mb-[8px] text-[12px] font-medium text-[#1f2937]">
                Your comparables will include
              </div>
              <div className="flex flex-col gap-[6px]">
                {[
                  "Trading peer comparables & valuation multiples",
                  "Growth benchmarks vs. peer average & median",
                  "Precedent transactions & deal terms",
                ].map((item) => (
                  <div
                    key={item}
                    className="flex items-center gap-[8px] text-[12px] text-[#374151]"
                  >
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#1D9E75"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    {item}
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between gap-[12px]">
              <div className="flex items-center gap-[6px] text-[12px] text-[#6b7280]">
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#9ca3af"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
                ~30-60 seconds
              </div>
              <div className="flex gap-[8px]">
                <button
                  type="button"
                  onClick={closeSearchEntityModal}
                  className="cursor-pointer rounded-full border border-solid border-[#d1d5db] bg-white px-[14px] py-[8px] text-[13px] font-medium text-[#374151] hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={goToSelectedEntityComparables}
                  disabled={!selectedEntity}
                  className={twMerge(
                    "rounded-full border-none px-[16px] py-[8px] text-[13px] font-medium",
                    selectedEntity
                      ? "cursor-pointer bg-smart-teal-300 text-white hover:bg-dark-teal"
                      : "cursor-not-allowed bg-smart-teal/50 text-white opacity-50"
                  )}
                >
                  Generate
                </button>
              </div>
            </div>
            <div className="mt-[20px] border-0 border-t border-solid border-[#E5E7EB]">
              <RecentComparablesSection />
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default EntityComparablesButton;
